import axios from 'axios'
import type { AxiosError, AxiosResponse } from 'axios'
import { store } from '../redux/global-store'
import { logOut } from '../features/authentication/presentation/redux/actions/log-out-action'
import { paths } from '../router/paths'

/**
 * Interceptador universal para capturar erros 401
 * Funciona tanto com Axios quanto com fetch()
 */

// Flag para evitar loops infinitos de redirecionamento
let isRedirecting = false

/**
 * Função central para lidar com erro 401
 */
export const handleUnauthorizedError = (): void => {
  // Evita múltiplos redirecionamentos simultâneos
  if (isRedirecting) return
  
  isRedirecting = true
  
  try {
    // Limpa dados de autenticação
    localStorage.removeItem('token')
    sessionStorage.removeItem('logged')
    
    // Limpa outros dados relacionados à sessão (opcional)
    const authKeys = ['user', 'userToken', 'authData']
    authKeys.forEach(key => {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    })
    
    // Dispara a ação de logout no Redux
    store.dispatch(logOut())
    
    // Aguarda um momento para o Redux processar
    setTimeout(() => {
      // Redireciona para login
      window.location.replace(paths.authentication.login)
    }, 100)
    
  } catch (error) {
    // Fallback direto em caso de erro
    window.location.replace('/login')
  }
}

/**
 * Setup do interceptador para fetch global
 */
export const setupUniversalFetchInterceptor = (): void => {
  // Backup da função fetch original
  const originalFetch = window.fetch
  
  // Substitui fetch por versão interceptada
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      const response = await originalFetch(input, init)
      
      // Intercepta erro 401
      if (response.status === 401) {
        handleUnauthorizedError()
      }
      
      return response
    } catch (error) {
      throw error
    }
  }
}

/**
 * Setup do interceptador para Axios
 */
export const setupUniversalAxiosInterceptor = (): void => {
  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        handleUnauthorizedError()
      }
      return Promise.reject(error)
    }
  )
}

/**
 * Inicializa todos os interceptadores
 */
export const initializeUniversalInterceptors = (): void => {
  setupUniversalFetchInterceptor()
  setupUniversalAxiosInterceptor()
}

/**
 * Função manual para testar o logout (útil para debugging)
 */
export const testLogout = (): void => {
  handleUnauthorizedError()
}